import { twMerge } from 'tailwind-merge';

import { Cross } from '@assets/icons';
import { Chip, PermissionAccess } from '@components/index';
import { UserPermissions } from '@enums/user-permissions';

import ChipSkeleton from './ChipSkeleton';
import { LabelItem } from './types';

type Props = {
  items: LabelItem[];
  onRemoveChip: (item: any, isRadioButtonDeSelectEnabled: boolean) => void;
  onClearAllChips?: () => void;
  isFilterMode?: boolean;
  isAssetInherited?: boolean;
  isAssetLocked?: boolean;
};

export const ActionChips = (props: Props) => {
  const {
    items,
    onRemoveChip,
    onClearAllChips,
    isFilterMode = false,
    isAssetInherited = true,
    isAssetLocked = false,
  } = props;

  const hasInheritedLabels = items.some(item => item.parentId);
  const hasChips = items.length > 0;
  const canClear = Boolean(onClearAllChips);

  const isClearAllIconVisible =
    !isAssetLocked &&
    canClear &&
    hasChips &&
    (isFilterMode ||
      !isAssetInherited ||
      (isAssetInherited && !hasInheritedLabels));

  const renderChips = ({
    showRemoveButtons,
    showClearAll,
  }: {
    showRemoveButtons: boolean;
    showClearAll: boolean;
  }) => (
    <div className="flex justify-between p-4 bg-surface-low-emphasis max-h-[100px] overflow-y-auto rounded-md border border-stroke-default">
      <div
        className={twMerge(
          'flex gap-2 flex-wrap rounded',
          showClearAll ? 'w-[92%]' : '',
        )}
      >
        {items.map((item, index) => {
          if (item) {
            const id = item.id;
            const name = item.name;
            const isInheritedLabel = !!item.parentId;
            const shouldShowRemoveButton =
              showRemoveButtons &&
              !isAssetLocked &&
              (isFilterMode || (isAssetInherited ? !isInheritedLabel : true));

            if (!name) {
              return <ChipSkeleton key={index} text={''} />;
            }

            return (
              <Chip
                key={`selection-${id}`}
                label={name}
                showTooltip={true}
                handleRemoveChip={
                  shouldShowRemoveButton
                    ? () => onRemoveChip(item, true)
                    : undefined
                }
                showRemoveButton={shouldShowRemoveButton}
              />
            );
          }

          return <ChipSkeleton key={index} />;
        })}
      </div>
      {showClearAll && (
        <div
          className="w-[22px] h-[22px] rounded-lg bg-gray-5 flex justify-center items-center cursor-pointer"
          onClick={onClearAllChips}
        >
          <Cross className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  );

  return (
    <PermissionAccess
      hasPermission={isFilterMode}
      permission={UserPermissions.MANAGE_BRAND_LIBRARY}
      AllowedContent={renderChips({
        showRemoveButtons: true,
        showClearAll: isClearAllIconVisible,
      })}
      DeniedContent={renderChips({
        showRemoveButtons: false,
        showClearAll: false,
      })}
    />
  );
};
